import { Check } from "lucide-react";
import { useRef, useState } from "react";
import { TASK_ROW_HEIGHT } from "@/common/constants";
import { cn } from "@/lib/utils";
import { useCustomAttribute } from "@/queries/custom-attribute-queries";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

interface NumberSelectProps {
    value: string | null;
    onChange: (value: string) => void;
    trigger: (content: React.ReactNode, value: string | null | undefined) => React.ReactElement;
    attributeId: number;
}

export function SelectOneSelect({ value, onChange, trigger, attributeId }: NumberSelectProps) {
    const [open, setOpen] = useState(false);
    const { data: customAttribute, isLoading } = useCustomAttribute(attributeId, { enabled: open });
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const handleOpenChange = (newOpen: boolean) => {
        if (newOpen) {
            // Use setTimeout to ensure the input is fully rendered and focused
            setTimeout(() => {
                inputRef.current?.select();
            }, 0);
        }
        setOpen(newOpen);
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
            <PopoverTrigger render={trigger(value, value)} />
            <PopoverContent sideOffset={-TASK_ROW_HEIGHT} align="start" className="w-full p-0 max-w-[450px]">
                <Command>
                    <CommandList>
                        <CommandEmpty>No options found.</CommandEmpty>
                        <CommandGroup>
                            <CommandItem
                                key="none"
                                value="__none__"
                                onSelect={() => {
                                    onChange("");
                                    setOpen(false);
                                }}
                            >
                                None
                                <Check className={cn("ml-auto", value === "" ? "opacity-100" : "opacity-0")} />
                            </CommandItem>
                            {!isLoading &&
                                customAttribute?.options.map((option: string) => (
                                    <CommandItem
                                        key={option}
                                        value={option}
                                        onSelect={(currentValue) => {
                                            onChange(currentValue === value ? "" : currentValue);
                                            setOpen(false);
                                        }}
                                    >
                                        {option}
                                        <Check
                                            className={cn("ml-auto", value === option ? "opacity-100" : "opacity-0")}
                                        />
                                    </CommandItem>
                                ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
